import { lazy } from 'react';

const RELOAD_FLAG = 'chunk-reload-attempted';

// A failed dynamic import after a redeploy is almost always a ChunkLoadError:
// the new build replaced the content-hashed chunk filenames, so a tab that was
// open across the deploy requests a chunk that no longer exists (404). Detect
// that case across the wording browsers/webpack use.
const isChunkLoadError = (error) =>
  error?.name === 'ChunkLoadError' ||
  /Loading chunk [\w-]+ failed/i.test(error?.message || '') ||
  /Loading CSS chunk [\w-]+ failed/i.test(error?.message || '');

/**
 * Drop-in replacement for `React.lazy` that recovers from a stale-chunk
 * ChunkLoadError by doing one full reload to pull the current build, instead of
 * leaving the user on a broken screen. The reload is guarded by a sessionStorage
 * flag and cleared on the next successful load, so a genuinely missing chunk
 * surfaces to the error boundary after a single attempt rather than looping.
 */
export const lazyWithReload = (factory) =>
  lazy(() =>
    factory()
      .then((mod) => {
        sessionStorage.removeItem(RELOAD_FLAG);
        return mod;
      })
      .catch((error) => {
        if (!isChunkLoadError(error)) throw error;
        if (sessionStorage.getItem(RELOAD_FLAG)) {
          sessionStorage.removeItem(RELOAD_FLAG);
          throw error;
        }
        sessionStorage.setItem(RELOAD_FLAG, '1');
        window.location.reload();
        // Suspend until the reload navigates away; never resolve this import.
        return new Promise(() => {});
      }),
  );
